import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ParentLayout from '../components/ParentLayout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Send, User } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MessageThreadPage = () => {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchThread();
  }, [threadId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchThread = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/messages/threads/${threadId}`,
        { withCredentials: true }
      );
      setThread(response.data.thread);
      setMessages(response.data.messages);
      setParticipants(response.data.participants);
    } catch (error) {
      console.error('Failed to fetch thread:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/messages/threads/${threadId}/reply`,
        { thread_id: threadId, body: newMessage },
        { withCredentials: true }
      );
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'league_admin':
      case 'super_admin':
        return 'bg-navy';
      case 'coach':
      case 'team_staff':
        return 'bg-red';
      default:
        return 'bg-light-blue';
    }
  };

  if (loading) {
    return (
      <ParentLayout>
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="h-96 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </ParentLayout>
    );
  }

  if (!thread) {
    return (
      <ParentLayout>
        <div className="max-w-3xl mx-auto px-4 py-8 text-center">
          <p className="text-gray-500">Thread not found</p>
          <Button onClick={() => navigate('/communications?tab=messages')} className="mt-4">
            Back to Communications
          </Button>
        </div>
      </ParentLayout>
    );
  }

  return (
    <ParentLayout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="message-thread">
        {/* Back Button */}
        <button 
          onClick={() => navigate('/communications?tab=messages')}
          className="flex items-center text-gray-600 hover:text-navy mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Communications
        </button>

        <Card className="h-[calc(100vh-280px)] flex flex-col">
          {/* Header */}
          <CardHeader className="border-b">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-navy">{thread.subject}</CardTitle>
                <div className="flex flex-wrap gap-2 mt-2">
                  {participants.filter(p => p.user_id !== user?.user_id).map(p => (
                    <Badge key={p.user_id} variant="outline" className="text-xs">
                      {p.name} ({p.role?.replace(/_/g, ' ')})
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardHeader>

          {/* Messages */}
          <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message, idx) => {
              const isMe = message.sender_id === user?.user_id;
              const participant = participants.find(p => p.user_id === message.sender_id);
              
              return (
                <div 
                  key={message.message_id}
                  className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[80%] ${isMe ? 'order-2' : ''}`}>
                    {!isMe && (
                      <div className="flex items-center space-x-2 mb-1">
                        <div className={`w-6 h-6 ${getRoleColor(participant?.role)} rounded-full flex items-center justify-center`}>
                          <User className="w-3 h-3 text-white" />
                        </div>
                        <span className="text-xs font-medium text-gray-700">
                          {message.sender_name || participant?.name}
                        </span>
                      </div>
                    )}
                    <div className={`rounded-2xl px-4 py-2 ${
                      isMe 
                        ? 'bg-navy text-white rounded-br-sm' 
                        : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                    }`}>
                      <p className="whitespace-pre-wrap">{message.body}</p>
                    </div>
                    <p className={`text-xs text-gray-400 mt-1 ${isMe ? 'text-right' : ''}`}>
                      {format(parseISO(message.sent_at), 'MMM d, h:mm a')}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </CardContent>

          {/* Compose */}
          <div className="border-t p-4">
            <form onSubmit={handleSendMessage} className="flex space-x-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                className="flex-1"
                disabled={sending}
              />
              <Button 
                type="submit" 
                className="bg-navy hover:bg-navy/90"
                disabled={!newMessage.trim() || sending}
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </Card>
      </div>
    </ParentLayout>
  );
};

export default MessageThreadPage;
